/**
 * Pieces on this page whose month and whose date disagree.
 *
 * The month that owes a piece is what the retainer counts; the day it goes
 * out is when it goes out. Both are real and neither is redundant — a reel
 * owed in August that posted on the 3rd of September is a late August reel,
 * not a September one. What went wrong was that they drifted apart in
 * silence: the plan lists a piece under both months and looks identical
 * either way, so a client's August could hold nine pieces against a target of
 * four and every screen in the app agreed with itself.
 *
 * Changing a date now moves the piece after it, so new drift does not
 * accumulate. This is for the drift already there — chiefly the migration
 * from the old sheets — and for anything that arrives through an API nobody
 * watched. It says what it found and offers to put it right, because the
 * alternative was a terminal script only one person could run.
 *
 * A piece somebody has deliberately pinned to its month is not drift and is
 * not listed. That is the whole reason pinning exists.
 */

import { AlertTriangle, ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import type { ContentItem } from '../data/types'
import { apiErrorMessage } from '../lib/api'
import { monthName } from '../lib/format'

/** The `YYYY-MM-01` a day falls in, or null when it is not a day. */
export function monthOfDay(iso: string | null | undefined): string | null {
  return typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(0, 7)}-01` : null
}

/**
 * The month a piece's own dates say it belongs to, or null when nobody has
 * said. What it posted on beats what it was scheduled for: one is a record
 * and the other is an intention.
 */
export function datedInto(c: ContentItem): string | null {
  return monthOfDay(c.postedDate) ?? monthOfDay(c.date)
}

/** Where a piece sits versus where its dates put it, or null when they agree. */
export function drifted(c: ContentItem): { from: string; to: string } | null {
  if (c.monthPinned === true) return null
  const owed = c.cycleMonth
  const dated = datedInto(c)
  if (owed === undefined || dated === null || owed === dated) return null
  return { from: owed, to: dated }
}

export function MonthHealth({
  content,
  month,
  clientId
}: {
  content: ContentItem[]
  month: string
  /** One client's id, or `all`. */
  clientId: string
}) {
  const { refileContent, clients } = useAppData()
  const { showToast } = useToast()
  const [busy, setBusy] = useState(false)

  const off = content
    .filter((c) => clientId === 'all' || c.clientId === clientId)
    .map((c) => ({ row: c, move: drifted(c) }))
    .filter((x): x is { row: ContentItem; move: { from: string; to: string } } => x.move !== null)

  if (off.length === 0) return null

  const nameOf = (id: string) => clients.find((cl) => cl.id === id)?.name ?? 'Somebody'

  async function move(id: string, to: string) {
    setBusy(true)
    try {
      await refileContent(id, to)
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function fixAll() {
    setBusy(true)
    let done = 0
    try {
      // One at a time, on purpose: each move is its own entry in the audit
      // trail, and a batch that half-failed would be the harder thing to read.
      for (const { row, move: m } of off) {
        await refileContent(row.id, m.to)
        done++
      }
      showToast(`${done} ${done === 1 ? 'piece' : 'pieces'} moved to the month they are dated in.`)
    } catch (err) {
      showToast(`${apiErrorMessage(err)} — ${done} of ${off.length} moved.`, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-card border border-amber-200 bg-amber-50/70 p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <AlertTriangle aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h2 className="font-display text-base font-semibold text-amber-900">
              {off.length === 1
                ? 'One piece is filed against a month its own date disagrees with'
                : `${off.length} pieces are filed against months their own dates disagree with`}
            </h2>
            <p className="text-xs text-amber-800/90">
              The month that owes a piece is what the deliverable counts read, so this is why{' '}
              {monthName(month)} may not add up. Moving one takes it out of its old month’s count and
              into the new one.
            </p>
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void fixAll()}
          className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
        >
          {busy ? 'Moving…' : `Move all ${off.length}`}
        </button>
      </div>

      <ul className="mt-3 flex flex-col divide-y divide-amber-200/70 border-t border-amber-200/70">
        {off.map(({ row, move: m }) => (
          <li key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate font-medium text-amber-900" title={row.title}>
              {row.title}
            </span>
            <span className="text-xs text-amber-800/80">{nameOf(row.clientId)}</span>
            <span className="flex items-center gap-1.5 text-xs tabular-nums text-amber-900">
              {monthName(m.from)}
              <ArrowRight aria-hidden className="h-3 w-3" />
              <strong>{monthName(m.to)}</strong>
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => void move(row.id, m.to)}
              className="rounded-lg border border-amber-300 px-2 py-1 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-60"
            >
              Move
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
